import { useCallback, useContext, useState } from 'react';
import { RobotVideo, type RobotVideoState } from '@verne/robot-video';

import { cameraFeedUrl, robotId } from '../../lib/config';
import { LocalCameraSourceContext, useVideoRobotId, videoAuth } from '../../lib/robot-video';
import { ControlBadge } from './control-badge';
import { MjpegImg } from './mjpeg-img';

interface CameraFeedProps {
  cameraId: string;
  /** Cognito id token; hub mode sends it to get an SFU token. */
  token?: string | null;
  alt?: string;
  className?: string;
}

/**
 * One live camera. Hub mode pulls from the SFU. Local mode uses the source in
 * `LocalCameraSourceContext`. The status overlay and the teleop / policy badge
 * are absolute, so the parent must be positioned.
 */
export function CameraFeed({ cameraId, token, alt, className }: CameraFeedProps) {
  const localSource = useContext(LocalCameraSourceContext);
  if (!robotId && localSource === 'mjpeg') {
    return (
      <>
        <MjpegImg src={cameraFeedUrl(cameraId)} alt={alt} className={className} />
        <ControlBadge />
      </>
    );
  }
  return <SfuFeed cameraId={cameraId} token={token} alt={alt} className={className} />;
}

function SfuFeed({ cameraId, token, alt, className }: CameraFeedProps) {
  const robot = useVideoRobotId();
  const [state, setState] = useState<RobotVideoState | null>(null);
  const onStatus = useCallback((s: RobotVideoState) => setState(s), []);

  const message = robot.error
    ? `Robot unknown: ${robot.error}`
    : state?.status === 'live'
      ? null
      : state?.status === 'retrying'
        ? `Reconnecting${state.error ? `: ${state.error}` : ''}`
        : state?.status === 'connecting'
          ? 'Connecting'
          : 'Waiting for camera';

  return (
    <>
      {robot.id && (
        <RobotVideo
          getAuth={videoAuth(token)}
          robotId={robot.id}
          camera={cameraId}
          onStatus={onStatus}
          aria-label={alt}
          className={className}
        />
      )}
      {message && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4 text-center text-xs font-medium text-muted">
          {message}
        </div>
      )}
      <ControlBadge />
    </>
  );
}
